import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Users, 
  UserPlus, 
  Trash, 
  ShieldCheck,
  User,
  Crown,
  Eye,
  EyeSlash,
  X
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "reviewer" });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/users/create`, newUser);
      toast.success(`Utilisateur ${newUser.name} créé avec succès`);
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "reviewer" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`);
      toast.success("Rôle mis à jour");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;

    try {
      await axios.delete(`${API}/users/${deleteDialog.user.id}`);
      toast.success("Utilisateur supprimé");
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const getRoleIcon = (role) => {
    if (role === "admin") {
      return <Crown weight="fill" className="w-4 h-4 text-cyber-volt" />;
    }
    return <User weight="duotone" className="w-4 h-4 text-slate-400" />;
  };

  return (
    <div data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-2">
            Gestion des utilisateurs
          </h1>
          <p className="text-slate-400">Administrez les accès à la plateforme</p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
          data-testid="add-user-btn"
        >
          <UserPlus weight="duotone" className="w-4 h-4 mr-2" />
          Ajouter un utilisateur
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyber-primary/10 rounded-sm flex items-center justify-center">
              <Users weight="duotone" className="w-5 h-5 text-cyber-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total utilisateurs</p>
              <p className="font-mono text-2xl">{users.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="cyber-card p-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyber-volt/10 rounded-sm flex items-center justify-center">
              <Crown weight="duotone" className="w-5 h-5 text-cyber-volt" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Administrateurs</p>
              <p className="font-mono text-2xl">
                {users.filter(u => u.role === "admin").length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="cyber-card p-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyber-success/10 rounded-sm flex items-center justify-center">
              <ShieldCheck weight="duotone" className="w-5 h-5 text-cyber-success" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Reviewers</p>
              <p className="font-mono text-2xl">
                {users.filter(u => u.role === "reviewer").length}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-cyber-primary font-mono">Chargement...</div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="cyber-card overflow-hidden"
        >
          <table className="w-full cyber-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Date d'inscription</th>
                <th className="w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`user-row-${user.id}`}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-cyber-primary/20 rounded-sm flex items-center justify-center">
                        <span className="text-sm text-cyber-primary font-mono">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-cyber-volt">(vous)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-slate-400">{user.email}</td>
                  <td>
                    {user.id === currentUser?.id ? (
                      <div className="flex items-center gap-2">
                        {getRoleIcon(user.role)}
                        <span className="capitalize">{user.role}</span>
                      </div>
                    ) : (
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                      >
                        <SelectTrigger className="w-32 h-8 cyber-input text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">
                            <div className="flex items-center gap-2">
                              <Crown weight="fill" className="w-3 h-3 text-cyber-volt" />
                              Admin
                            </div>
                          </SelectItem>
                          <SelectItem value="reviewer">
                            <div className="flex items-center gap-2">
                              <User weight="duotone" className="w-3 h-3" />
                              Reviewer
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="text-slate-400 text-sm">
                    {new Date(user.created_at).toLocaleDateString("fr-FR")}
                  </td>
                  <td>
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, user })}
                        className="text-slate-400 hover:text-cyber-danger hover:bg-cyber-danger/10"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash weight="duotone" className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-cyber-surface border-cyber-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus weight="duotone" className="w-5 h-5 text-cyber-primary" />
              Nouvel utilisateur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Nom complet *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Jean Dupont"
                className="cyber-input"
                data-testid="new-user-name"
              />
            </div>

            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="jean.dupont@entreprise.com"
                className="cyber-input"
                data-testid="new-user-email"
              />
            </div>

            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  className="cyber-input pr-10"
                  data-testid="new-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Rôle</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="cyber-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewer">
                    <div className="flex items-center gap-2">
                      <User weight="duotone" className="w-4 h-4" />
                      Reviewer - Consultation et analyse
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Crown weight="fill" className="w-4 h-4 text-cyber-volt" />
                      Admin - Gestion complète
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCreateDialogOpen(false)}
              className="text-slate-400"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creating || !newUser.name || !newUser.email || !newUser.password}
              className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
              data-testid="confirm-create-user"
            >
              {creating ? "Création..." : "Créer l'utilisateur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-cyber-surface border-cyber-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Êtes-vous sûr de vouloir supprimer <span className="text-white font-medium">{deleteDialog.user?.name}</span> ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-cyber-border text-slate-400 hover:bg-cyber-surface-hover hover:text-white">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-cyber-danger text-white hover:bg-cyber-danger/80"
              data-testid="confirm-delete-user"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
